import { useRef, useState } from 'react';
import { Check, Plus, Users2, X } from 'lucide-react';
import {
  EMPLOYMENT_TYPES, EMPLOYMENT_TYPE_LABELS, LEAVE_KINDS, LEAVE_KIND_LABELS, LEAVE_STATUS_LABELS,
  type Employee, type EmploymentType, type LeaveRequestInput, type Workspace,
} from '@unitly/contracts';
import {
  useCancelLeaveRequest, useCreateLeaveRequest, useEmployees, useHrConfig, useHrSettings, useLeaveRequests, useReviewLeaveRequest, useUpdateEmployee,
} from '../../api/queries.ts';
import { useOrgCtx } from '../../app/org-context.tsx';
import { Sheet } from '../../ui/Sheet.tsx';
import { Avatar, EmptyState, PageLoader, Segmented } from '../../ui/primitives.tsx';
import { useToast } from '../../ui/Toast.tsx';
import { ApiError } from '../../api/client.ts';

const today = () => new Date().toISOString().slice(0, 10);
const days = (a: string, b: string) => Math.round((+new Date(b) - +new Date(a)) / 86_400_000) + 1;

export function HrView({ workspace }: { workspace: Workspace }) {
  const { orgId } = useOrgCtx();
  const config = useHrConfig(orgId, workspace.id);
  const [tab, setTab] = useState<'requests' | 'people'>('requests');
  const [requesting, setRequesting] = useState(false);
  const requestSeq = useRef(0);
  const [editing, setEditing] = useState<Employee | null>(null);

  if (config.isLoading) return <PageLoader />;
  const cfg = config.data;
  if (!cfg?.enabled) return <EnableHr workspace={workspace} canEnable={Boolean(cfg?.permissions.admin)} />;

  return (
    <>
      <div className="toolbar">
        <Segmented label="Sezione HR" value={tab} onChange={setTab} options={[{ value: 'requests', label: 'Assenze' }, { value: 'people', label: 'Persone' }]} />
        <span className="spacer" />
        {tab === 'requests' && <button type="button" className="btn btn-primary btn-sm" onClick={() => { requestSeq.current += 1; setRequesting(true); }}><Plus size={16} /> Assenza</button>}
      </div>
      {tab === 'requests' && <LeaveList workspace={workspace} admin={cfg.permissions.admin} />}
      {tab === 'people' && <EmployeeList workspace={workspace} admin={cfg.permissions.admin} onEdit={setEditing} />}
      <LeaveRequestSheet key={`req-${requestSeq.current}`} workspace={workspace} open={requesting} onClose={() => setRequesting(false)} />
      <EmployeeSheet key={editing?.userId ?? 'closed'} workspace={workspace} employee={editing} open={editing !== null} onClose={() => setEditing(null)} />
    </>
  );
}

function EnableHr({ workspace, canEnable }: { workspace: Workspace; canEnable: boolean }) {
  const { orgId } = useOrgCtx();
  const enable = useHrSettings(orgId, workspace.id);
  const toast = useToast();
  return (
    <div className="card card-pad" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: 28 }}>
      <span className="empty" style={{ padding: 0 }}><span className="icon" style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}><Users2 size={22} /></span></span>
      <strong style={{ fontSize: 18 }}>Risorse umane di {workspace.name}</strong>
      <p style={{ margin: 0, color: 'var(--label-2)', maxWidth: '46ch', fontSize: 15 }}>Profili delle persone del team e richieste di ferie e permessi, con approvazione.</p>
      {canEnable ? (
        <button type="button" className="btn btn-primary" disabled={enable.isPending}
          onClick={() => enable.mutate(undefined, { onSuccess: () => toast('HR attivato'), onError: (e) => toast(e.message) })}>
          Attiva l’HR
        </button>
      ) : (
        <p className="hint">Chiedi a chi gestisce lo spazio di attivarlo.</p>
      )}
    </div>
  );
}

function LeaveList({ workspace, admin }: { workspace: Workspace; admin: boolean }) {
  const { orgId, me } = useOrgCtx();
  const requests = useLeaveRequests(orgId, workspace.id);
  const review = useReviewLeaveRequest(orgId, workspace.id);
  const cancel = useCancelLeaveRequest(orgId, workspace.id);
  const toast = useToast();

  if (requests.isLoading) return <PageLoader />;
  if (requests.data?.length === 0) return <div className="card"><EmptyState tone="neutral" title="Nessuna richiesta">{admin ? 'Nessuna assenza da revisionare.' : 'Non hai richieste di assenza.'}</EmptyState></div>;

  return (
    <div className="card list">
      {requests.data?.map((r) => (
        <div key={r.id} className="list-row" style={{ cursor: 'default' }}>
          <Avatar id={r.user.id} name={r.user.displayName} size={36} />
          <span className="grow">
            <div>{admin && r.user.id !== me.user.id ? `${r.user.displayName} · ` : ''}{LEAVE_KIND_LABELS[r.kind]} · {r.days} {r.days === 1 ? 'giorno' : 'giorni'}</div>
            <div className="secondary">{new Date(r.startDate).toLocaleDateString('it-IT')} – {new Date(r.endDate).toLocaleDateString('it-IT')}{r.note ? ` · ${r.note}` : ''}</div>
          </span>
          <span className="pill" data-cat={r.status === 'approved' ? 'done' : r.status === 'rejected' ? 'canceled' : undefined}>{LEAVE_STATUS_LABELS[r.status]}</span>
          {r.canReview && (
            <span style={{ display: 'flex', gap: 4 }}>
              <button type="button" className="icon-btn muted" aria-label="Approva" onClick={() => review.mutate({ id: r.id, status: 'approved' }, { onError: (e) => toast(e instanceof ApiError ? e.message : 'Operazione non riuscita') })}><Check size={17} color="var(--green)" /></button>
              <button type="button" className="icon-btn muted" aria-label="Rifiuta" onClick={() => review.mutate({ id: r.id, status: 'rejected' }, { onError: (e) => toast(e instanceof ApiError ? e.message : 'Operazione non riuscita') })}><X size={17} color="var(--red)" /></button>
            </span>
          )}
          {!r.canReview && r.canCancel && (
            <button type="button" className="icon-btn muted" aria-label="Annulla richiesta" onClick={() => cancel.mutate(r.id)}><X size={17} /></button>
          )}
        </div>
      ))}
    </div>
  );
}

function LeaveRequestSheet({ workspace, open, onClose }: { workspace: Workspace; open: boolean; onClose: () => void }) {
  const { orgId } = useOrgCtx();
  const create = useCreateLeaveRequest(orgId, workspace.id);
  const toast = useToast();
  const [form, setForm] = useState<LeaveRequestInput>({ kind: 'vacation', startDate: today(), endDate: today(), days: 1, note: null });

  return (
    <Sheet open={open} onClose={onClose} variant="center" title="Richiedi assenza" labelledBy="leave-title"
      footer={
        <div style={{ display: 'flex', gap: 8, width: '100%' }}>
          <span className="spacer" />
          <button type="button" className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button type="button" className="btn btn-primary" disabled={create.isPending}
            onClick={() => create.mutate({ ...form, days: days(form.startDate, form.endDate) }, {
              onSuccess: onClose, onError: (e) => toast(e instanceof ApiError ? e.message : 'Richiesta non riuscita'),
            })}>
            Invia richiesta
          </button>
        </div>
      }>
      <Segmented label="Tipo" value={form.kind} onChange={(kind) => setForm({ ...form, kind })} options={LEAVE_KINDS.map((k) => ({ value: k, label: LEAVE_KIND_LABELS[k] }))} />
      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        <label className="field" style={{ flex: 1 }}><span>Dal</span><input className="input" type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} /></label>
        <label className="field" style={{ flex: 1 }}><span>Al</span><input className="input" type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} /></label>
      </div>
      <p className="hint">{days(form.startDate, form.endDate)} {days(form.startDate, form.endDate) === 1 ? 'giorno' : 'giorni'}</p>
      <label className="field"><span>Nota (opzionale)</span><input className="input" value={form.note ?? ''} onChange={(e) => setForm({ ...form, note: e.target.value || null })} /></label>
    </Sheet>
  );
}

function EmployeeList({ workspace, admin, onEdit }: { workspace: Workspace; admin: boolean; onEdit: (e: Employee) => void }) {
  const { orgId } = useOrgCtx();
  const employees = useEmployees(orgId, workspace.id);
  if (employees.isLoading) return <PageLoader />;
  return (
    <div className="card list">
      {employees.data?.map((e) => (
        <div key={e.userId} className="list-row" role={admin ? 'button' : undefined} tabIndex={admin ? 0 : undefined}
          style={{ cursor: admin ? 'pointer' : 'default' }} onClick={() => admin && onEdit(e)} onKeyDown={(ev) => admin && ev.key === 'Enter' && onEdit(e)}>
          <Avatar id={e.userId} name={e.displayName} size={36} />
          <span className="grow">
            <div>{e.displayName}</div>
            <div className="secondary">{e.jobTitle ?? EMPLOYMENT_TYPE_LABELS[e.employmentType]} · {e.leaveBalanceDays} giorni residui{e.pendingLeaveDays > 0 ? ` · ${e.pendingLeaveDays} in attesa` : ''}</div>
          </span>
        </div>
      ))}
    </div>
  );
}

function EmployeeSheet({ workspace, employee, open, onClose }: { workspace: Workspace; employee: Employee | null; open: boolean; onClose: () => void }) {
  const { orgId } = useOrgCtx();
  const update = useUpdateEmployee(orgId, workspace.id);
  const toast = useToast();
  const [form, setForm] = useState({ jobTitle: employee?.jobTitle ?? '', employmentType: employee?.employmentType ?? 'full_time' as EmploymentType, startDate: employee?.startDate ?? '', leaveBalanceDays: employee?.leaveBalanceDays ?? 20 });
  const [key, setKey] = useState(employee?.userId ?? '');
  if ((employee?.userId ?? '') !== key) {
    setForm({ jobTitle: employee?.jobTitle ?? '', employmentType: employee?.employmentType ?? 'full_time', startDate: employee?.startDate ?? '', leaveBalanceDays: employee?.leaveBalanceDays ?? 20 });
    setKey(employee?.userId ?? '');
  }
  if (!employee) return null;

  return (
    <Sheet open={open} onClose={onClose} variant="center" title={employee.displayName} labelledBy="employee-title"
      footer={
        <div style={{ display: 'flex', gap: 8, width: '100%' }}>
          <span className="spacer" />
          <button type="button" className="btn btn-secondary" onClick={onClose}>Annulla</button>
          <button type="button" className="btn btn-primary" disabled={update.isPending}
            onClick={() => update.mutate({ userId: employee.userId, patch: { ...form, startDate: form.startDate || null } }, { onSuccess: onClose, onError: (e) => toast(e instanceof ApiError ? e.message : 'Salvataggio non riuscito') })}>
            Salva
          </button>
        </div>
      }>
      <label className="field"><span>Ruolo</span><input className="input" value={form.jobTitle} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} placeholder="es. Copywriter" /></label>
      <div className="field">
        <span>Tipo di contratto</span>
        <select className="input" value={form.employmentType} onChange={(e) => setForm({ ...form, employmentType: e.target.value as EmploymentType })}>
          {EMPLOYMENT_TYPES.map((t) => <option key={t} value={t}>{EMPLOYMENT_TYPE_LABELS[t]}</option>)}
        </select>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <label className="field" style={{ flex: 1 }}><span>Data di inizio</span><input className="input" type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} /></label>
        <label className="field" style={{ flex: 1 }}><span>Giorni ferie/anno</span><input className="input" type="number" min={0} value={form.leaveBalanceDays} onChange={(e) => setForm({ ...form, leaveBalanceDays: Number(e.target.value) })} /></label>
      </div>
    </Sheet>
  );
}
